import React from 'react';
import SeoHead from '../components/SeoHead';
import { getOptimizedImageUrl } from '../lib/cms';

export default function AltAnsigtsterapiPage() {
  return (
    <>
      <SeoHead
        title="ALT Ansigtsterapi – Niels Boye | Ibjerget på Midtfyn"
        description="ALT Ansigtsterapi v. Niels Boye på Ibjerget. Forløsning af følelser, spændinger og traumer gennem blidt muskelarbejde i ansigt, hals og nakke."
        canonicalUrl="https://www.ibjerget.dk/alt-ansigtsterapi"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#381d6e] text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/alt-ansigtsterapi.webp', { width: 1400, quality: 80, fit: 'cover' })}
            alt="Niels udfører ALT Ansigtsterapi på Ibjerget"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/75 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-sparkles" aria-hidden="true"></i>
            <span>Terapeut Niels Boye</span>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#faf5f9] tracking-tight leading-tight">
            ALT Ansigtsterapi
          </h1>
          <p className="mt-6 text-base sm:text-xl text-[#f7eefa] max-w-2xl mx-auto leading-relaxed font-normal">
            Det levende, udtryksfulde ansigt som spejl for følelseslivet og kroppens sande skønhed.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#booking"
              className="px-8 py-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md transition-colors"
            >
              Book tid hos Niels
            </a>
            <a
              href="tel:66157902"
              className="px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone mr-2 text-[#f7eefa]"></i>
              Ring 66 15 79 02
            </a>
          </div>
        </div>
      </section>

      {/* Main Content & Image */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Text */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
                Om behandlingen
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                Det levende udtryksfulde ansigt
              </h2>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Ansigtets muskler har den særlige kvalitet, at de direkte spejler den følelsesmæssige tilstand. Ser man trist ud, er det fordi man er trist. De muskler, der viser det triste ansigt, bærer nogle følelser med underliggende (ubevidste) oplevelser.
                </p>
                <p>
                  Når jeg bringer dig i en tilstand, hvor du giver slip på dagligdagens bekymringer og stress, vil jeg med et blidt træk i en specifik muskel sætte fokus på de bagvedliggende følelser/traumer og dermed give mulighed for forløsning. Nogle oplever, at kroppen giver slip som i et lettelsens suk, mens andre oplever de følelser/traumer, der ligger bag. Nogen gange arbejder vi psykoterapeutisk med at bevidstgøre og dermed forløse de problematikker der ligger, og andre gange er det bare en forløsning.
                </p>
                <p>
                  Jeg (Niels) er uddannet i ALT Ansigts Løftnings Teknik af Stanley Rosenberg, og når jeg har valgt at ændre navnet, er det fordi mit fokus ikke ligger i den fysiske skønhed (selv om teknikken også udglatter rynker og retter op på skævheder i ansigtet), men derimod det levende udtryksfulde ansigt, som for mig repræsenterer den sande skønhed.
                </p>
                <p>
                  Jeg mødte på et tidspunkt en kvinde, der fortalte, at hun havde prøvet at få den slags behandlinger 2 år tidligere hos en anden terapeut. Jeg kunne se, hvordan hun strålede med liv i den øverste del af ansigtet mens den nederste del var knap så udtryksfuld, og kunne derfor fortælle hende, hvor mange behandlinger hun havde modtaget fordi vi er blevet undervist i at arbejde med ansigtet i en bestemt rækkefølge.
                </p>
                <p>
                  Forløbet består som udgangspunkt af 6 behandlinger, hvor vi gennemgår samtlige muskler i hoved, ansigt, hals og nakke, men kan også arbejde specifikt med f.eks. et skævt bid eller spændingshovedpiner.
                </p>
                <p>
                  Teknikken kan også bruges andre steder end i ansigtet ved f.eks. en frossen skulder eller tennisalbue.
                </p>
              </div>

              {/* Behandlingsfaser */}
              <div className="pt-6 space-y-5">
                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Indledende samtale
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Jeg kikker på dit ansigt og din kropsholdning og du fortæller mig, hvad formålet med behandlingen er. Det kan være hovedpine, problemer med din bidefunktion, at møde dig selv i en mere autentisk udgave eller et friere følelsesliv.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Selve behandlingen
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Du ligger på ryggen og jeg hjælper dig til at slippe de ting, der forhindrer dig i at slappe af. Derefter sætter jeg fokus på én muskel ad gangen og forløser det følelsesmæssige indhold, du er parat til at slippe.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Integration
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Efter en ansigtsbehandling kan du de første dage opleve dig let til tårer, eller at du giver din partner et riv af. Det er udtryk for, at de ting du har sluppet kommer op til overfladen for at hilse af.
                  </p>
                  <p className="text-sm text-[#9b1b66] font-medium mt-2">
                    Der skal typisk gå en til to uger, før du er parat til endnu en behandling.
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Concrete Image & Practical Info */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Photo: alt-ansigtsterapi.webp */}
              <div className="rounded-3xl overflow-hidden shadow-lg border border-[#edd5e8] bg-white">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/alt-ansigtsterapi.webp', { width: 800, quality: 85, fit: 'cover' })}
                  alt="Niels Boye udfører ALT Ansigtsterapi på en klient"
                  className="w-full h-auto object-cover"
                />
                <div className="p-4 bg-[#fcf8fb] text-xs text-[#9b1b66] italic text-center border-t border-[#edd5e8]">
                  Niels arbejder direkte og fokuseret med ansigtets muskulatur på briksen
                </div>
              </div>

              {/* Praktisk information */}
              <div id="booking" className="bg-[#381d6e] text-[#faf5f9] rounded-3xl p-8 border border-[#5f2a99] shadow-lg space-y-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#e895c8] block mb-1">
                    Fakta & Booking
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-white">
                    Praktisk information
                  </h3>
                </div>

                <div className="space-y-3.5 text-sm">
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Varighed:</span>
                    <span className="font-semibold text-white">60-75 minutter</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Pris:</span>
                    <span className="font-semibold text-white">500 kr.</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Sted:</span>
                    <span className="font-semibold text-white text-right">Ibjergvej 12, 5792 Årslev</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Afbud:</span>
                    <span className="font-semibold text-white">Senest 24 timer før</span>
                  </div>
                </div>

                {/* Booking til Niels Boye */}
                <div className="pt-4 border-t border-[#5f2a99] space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wider text-[#e895c8] font-semibold">
                      Behandler
                    </p>
                    <p className="text-lg font-serif font-bold text-white">
                      Niels Boye
                    </p>
                    <p className="text-xs text-[#f7eefa]">
                      Kontakt Niels direkte for tidsbestilling og spørgsmål:
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 pt-2">
                    <a
                      href="tel:66157902"
                      className="flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                    >
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                      <span>Ring på 66 15 79 02</span>
                    </a>
                    <a
                      href="mailto:niels@ibjerget.dk"
                      className="flex items-center justify-center gap-3 w-full py-3 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
                    >
                      <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                      <span>Skriv til niels@ibjerget.dk</span>
                    </a>
                  </div>

                  <div className="p-3 bg-[#4a2282] rounded-xl text-xs text-[#f7eefa] leading-relaxed">
                    <p>
                      <strong>Bemærk:</strong> Niels er Elektro Hyper Sensitiv, og derfor skal mobiltelefon og eventuel smartwatch være slukket under behandlingen.
                    </p>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}
